import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import noResultsImageSrc from '../../../../assets/images/noResults.png';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AnalyticsTableMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {SettingsChannelsOverviewTabsEnum} from '../../../../models/url/models/channelsUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsChatBotsEditorTabsEnum} from '../../../../models/url/settingsUrlModel';
import {downloadFileAsync} from '../../../../services/browser';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getAnalyticsDatetimes} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {
  getAnalyticsReportIsLoading,
  makeGetAnalyticsMetric,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';
import {useAnalyticsFilter} from '../../../helpers/hooks/useAnalyticsFilter';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ReportTable} from '../../reportElements/table/reportTable';
import {AnalyticsTooltip} from '../../reportElements/tooltips/analyticsTooltips';
import {makeCsvFileData} from './chatbotReportHelpers';

/*
 * Constants.
 */

const reportSectionId = ReportElementIdsEnum.CHATBOT_AI_ANALYTICS;

const defaultAiAnswersColumnIds = [
  'chatbot',
  // 'ai_participation_rate',
  'ai_reached_count',
  'ai_answer_rate',
  'ai_answer_count',
  // 'ai_success_rate',
  'ai_resolution_rate',
  'ai_resolution_count',
];

/*
 * Props.
 */

interface ChatbotsAiAnswersTableProps {
  namespace: string;
}

interface StyledInfoImgProps {
  $imageWidth: number;
  $imageHeight: number;
}

/*
 * Styles.
 */

const StyledInfoTextWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledInfoHeaderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  line-height: 20px;
  max-width: 480px;
  white-space: normal;
  text-align: center;
`;

const StyledInfoBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

const StyledInfoWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 310px;
  gap: 24px;
`;

const StyledInfoImg = styled.img<StyledInfoImgProps>`
  width: ${(p) => p.$imageWidth}px;
  height: ${(p) => p.$imageHeight}px;
`;

const StyledAiAnswersTableEmptyStateWrapperDiv = styled.div`
  grid-column: span 12;
  max-width: 100%;
`;

const StyledEmptyTableStateBorderDiv = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 20px;
  border: 2px solid ${(p) => p.theme.greys.shade30};
  padding: 28px 0;
`;

const StyledTitleWithTooltipWrapperDiv = styled.div`
  ${flexCenter};
  margin-bottom: 16px;
`;

const StyledAnalyticsTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
`;

const StyledAnalyticsTooltip = styled(AnalyticsTooltip)`
  margin-left: 8px;
`;

const StyledExportDropdownDiv = styled.div`
  grid-column: span 12;

  display: flex;
  justify-content: center;
`;

const StyledExportButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

/*
 * Component.
 */

export const ChatbotAiAnswersTable: FC<ChatbotsAiAnswersTableProps> = ({namespace}) => {
  const {navigateToLink} = useLinkNavigation();
  const localize = useLocalize();

  const company = useAppSelector(getCompany);

  const isLoading = useAppSelector(getAnalyticsReportIsLoading);
  const [aiTableData] = useAppSelector(
    makeGetAnalyticsMetric(AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE),
  );

  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const [selectedDecisionTreeIds] = useAnalyticsFilter(AnalyticsFilterTypesEnum.CHATBOTS);

  const selectedDecisionTreeId =
    selectedDecisionTreeIds?.length === 1 ? selectedDecisionTreeIds[0] : undefined;

  const onEditChatbotFlowClick = () => {
    // If we retrieved the selectedChatbotId, onClick should edit that chatbot, else view all chatbots.
    if (selectedDecisionTreeId) {
      return navigateToLink(
        buildSettingsPath({
          namespace,
          rootPage: SettingsRootPagesEnum.CHAT_BOTS,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          decisionTreeId: selectedDecisionTreeId,
          tabPage: SettingsChatBotsEditorTabsEnum.BUILDER,
        }),
      );
    }

    return navigateToLink(
      buildSettingsPath({
        namespace,
        rootPage: SettingsRootPagesEnum.CHANNELS,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
        tab: SettingsChannelsOverviewTabsEnum.CHAT_BOTS,
      }),
    );
  };

  const hasAiAnswersNode = !aiTableData?.isEmpty;
  const renderAiAnswersEmptyState = () => {
    const {label: title, tooltip} = getMetricLabels(AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE);

    return (
      <StyledAiAnswersTableEmptyStateWrapperDiv>
        <StyledTitleWithTooltipWrapperDiv>
          <StyledAnalyticsTitleDiv>{title}</StyledAnalyticsTitleDiv>
          <StyledAnalyticsTooltip tooltip={tooltip} reportSectionId={reportSectionId} />
        </StyledTitleWithTooltipWrapperDiv>
        <div>
          <StyledEmptyTableStateBorderDiv>
            <StyledInfoWrapperDiv>
              <StyledInfoImg
                $imageWidth={92}
                $imageHeight={67}
                src={noResultsImageSrc}
                alt={localize(
                  defineMessage({
                    id: 'analytics-metric-chatbot-no-ai-answers-header',
                    message: 'No AI Answers step in your chatbot yet',
                  }),
                )}
              />
              <StyledInfoTextWrapperDiv>
                <StyledInfoHeaderDiv>
                  <Trans id="analytics-metric-chatbot-no-ai-answers-header">
                    No AI Answers step in your chatbot yet
                  </Trans>
                </StyledInfoHeaderDiv>
                <StyledInfoBodyDiv>
                  <Trans id="analytics-metric-chatbot-no-ai-answers-body">
                    Resolve chatbot inquiries instantly with AI, powered by your chosen knowledge sources.
                  </Trans>
                </StyledInfoBodyDiv>
              </StyledInfoTextWrapperDiv>
              <StyledButtonWrapperDiv>
                <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onEditChatbotFlowClick}>
                  <Trans id="analytics-metric-chatbot-no-ai-answers-edit-chatbot-button">
                    Edit chatbot flow
                  </Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.LINK}
                  rightIcon="externalLinkSmall"
                  onClick={() => openInBrowser(`${helpCenterUrl}/en/articles/2073`)}
                >
                  <Trans id="analytics-metric-chatbot-no-ai-answers-learn-more-button">Learn more</Trans>
                </Button>
              </StyledButtonWrapperDiv>
            </StyledInfoWrapperDiv>
          </StyledEmptyTableStateBorderDiv>
        </div>
      </StyledAiAnswersTableEmptyStateWrapperDiv>
    );
  };

  if (!hasAiAnswersNode) {
    return renderAiAnswersEmptyState();
  }

  return (
    <ReportTable
      reportSectionId={reportSectionId}
      metric={AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE}
      defaultColumnIds={defaultAiAnswersColumnIds}
      isLoading={isLoading}
      disableSortBy
      disableSelection
      disableGlobalFilter
      disableTablePagination
      renderDropdown={() =>
        selectedDecisionTreeId &&
        startDate &&
        endDate &&
        renderExportCsvButton(company, selectedDecisionTreeId, startDate, endDate)
      }
    />
  );
};

/*
 * Helpers.
 */

function renderExportCsvButton(
  company: CompanyModel,
  decisionTreeId: number,
  startDate: DateTime,
  endDate: DateTime,
) {
  const resolvedAnswersFile = makeCsvFileData(
    company,
    decisionTreeId,
    startDate,
    endDate,
    'ai_answer_resolutions',
  );
  const answersFile = makeCsvFileData(company, decisionTreeId, startDate, endDate, 'ai_answers');

  const onDownloadAllClick = () => {
    downloadFileAsync(answersFile.url, answersFile.name);
  };

  const onDownloadResolvedClick = () => {
    downloadFileAsync(resolvedAnswersFile.url, resolvedAnswersFile.name);
  };

  return (
    <StyledExportDropdownDiv>
      <PopoverCoordinator
        renderButton={() => (
          <Button>
            <StyledExportButtonDiv>
              <Trans id="chatbot-report-header-filters-csv-export-button">Export AI Answers history</Trans>
            </StyledExportButtonDiv>
          </Button>
        )}
        renderModal={(popoverProps) => (
          <SimpleDropdown {...popoverProps}>
            <SimpleDropdownItem onSelect={onDownloadAllClick}>
              <Trans id="chatbot-report-header-filters-csv-export-dropdown-option-all">
                All conversations
              </Trans>
            </SimpleDropdownItem>
            <SimpleDropdownItem onSelect={onDownloadResolvedClick}>
              <Trans id="chatbot-report-header-filters-csv-export-dropdown-option-resolved">
                All resolved conversations
              </Trans>
            </SimpleDropdownItem>
          </SimpleDropdown>
        )}
      />
    </StyledExportDropdownDiv>
  );
}
